import React, { memo, useCallback, useEffect, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useWindowSize } from 'react-use';
import classnames from 'classnames';
import { useScrollbarStyles } from 'modules/common/styles';
import { ErrorPage } from 'modules/common/routes/ErrorPage';
import config from 'config';
import { Connectors, defaultConnectors } from 'types/types';
import { useSidePanelContext } from 'modules/rootPage';
import { RepliconMenuFooter } from 'modules/common/RepliconMenu';
import { useRepliconNavigationContext } from 'modules/common/hooks/RepliconNavigationProvider';
import { getRequest, postRequest } from 'utils/helper';
import { AllConnectorsListPage } from './AllConnectorsListPage';
import { ConnectorHeader } from './ConnectorHeader';
import { ConnectorListPageSkeleton } from './ConnectorListPageSkeleton';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'absolute',
    height: '-webkit-fill-available',
    width: ({ width }: { width: number }) => width,
    overflowX: 'hidden',
    overflowY: 'auto',
    background: theme.palette.information.pageBackground,
    display: 'flex',
    flexDirection: 'column'
  },
  copyright: {
    marginTop: 'unset'
  }
}));

export const AllConnectorsTab: React.FC = memo(() => {
  const [searchValue, setSearchValue] = React.useState('');
  const { pinned } = useSidePanelContext();
  const { width } = useWindowSize();
  const classes = useStyles({
    width: pinned ? width - 240 : width - 64
  });
  const navigationMetadata = useRepliconNavigationContext();
  const classesScrollbar = useScrollbarStyles();
  const [data, setConnectorData] = React.useState<Connectors | undefined>();
  const [featureFlagData, setFeatureFlagData] = React.useState<string[]>([]);
  const [showErrorPage, setShowErrorPage] = React.useState<boolean>(false);
  const [showComputerEase, setShowComputerEase] = React.useState<boolean>(
    false
  );

  const fetchFeatureFlagData = useCallback(async (companyKey: string) => {
    try {
      const body = {
        companyKey: companyKey,
        featureFlagKey: 'IP2_4464_connector_migration',
        defaultValue: { AvailableConnectors: [] }
      };
      const res = await postRequest(
        '/integration-settings-api/feature-flag',
        body,
        false
      );
      if (res.data) {
        const {
          featureFlagValue: { AvailableConnectors }
        } = res.data;
        setFeatureFlagData(AvailableConnectors);
      }
    } catch (error: any) {
      if (error.response) {
        setFeatureFlagData(defaultConnectors.wave1);
      }
    }
  }, []);

  const fetchComputerEaseFeatureFlag = useCallback(
    async (companyKey: string) => {
      try {
        const body = {
          companyKey: companyKey,
          featureFlagKey: 'map-2-2102-replicon-ce-integration-integration-ui',
          defaultValue: false
        };
        const res = await postRequest(
          '/integration-settings-api/feature-flag',
          body,
          false
        );
        if (res.data) {
          const { featureFlagValue } = res.data;
          setShowComputerEase(featureFlagValue);
        }
      } catch (error: any) {
        setShowComputerEase(false);
      }
    },
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRequest(
          '/integration-settings-api/all-installed-connectors',
          {},
          false
        );
        if (response.data) {
          const {
            installedConnectors,
            companyKey,
            isPsaProduct
          } = response.data;
          await fetchFeatureFlagData(companyKey);
          await fetchComputerEaseFeatureFlag(companyKey);
          const mergedConnectorData = config.integrations.map(
            (connectorItem: any) => {
              const { shortName } = connectorItem;
              if (installedConnectors) {
                const matchingConnector = installedConnectors.find(
                  (installedConnector: { connectorName: string }) =>
                    installedConnector.connectorName === shortName
                );

                if (
                  matchingConnector &&
                  matchingConnector.installed &&
                  matchingConnector.isDagsError
                ) {
                  connectorItem.installed = matchingConnector.installed;
                  connectorItem.isDagsError = matchingConnector.isDagsError;
                  connectorItem.installMethod = matchingConnector.installMethod;
                }
              }

              return connectorItem;
            }
          );

          setConnectorData({
            connectors: { items: mergedConnectorData },
            companyKey: companyKey,
            isPsaProduct: isPsaProduct
          });
        }
      } catch (error: any) {
        if (error.response) {
          setShowErrorPage(true);
        }
      }
    };
    fetchData();
  }, [fetchFeatureFlagData, fetchComputerEaseFeatureFlag]);

  const matchingConnectors = useMemo(() => {
    if (!data || !data.connectors.items || !featureFlagData) return;

    const filteredItems = data.connectors.items.filter(
      ({ shortName }: { shortName: string }) => {
        if (shortName === 'computerease') {
          return showComputerEase;
        }
        return featureFlagData.includes(shortName);
      }
    );

    return {
      ...data,
      connectors: { items: filteredItems }
    };
  }, [data, featureFlagData, showComputerEase]);

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }

  return (
    <div
      className={classnames(classes.container, classesScrollbar.scrollbar)}
      data-testid="render-home-page"
    >
      <ConnectorHeader setSearchValue={setSearchValue} />
      {matchingConnectors ? (
        <AllConnectorsListPage
          searchValue={searchValue}
          data={matchingConnectors}
        />
      ) : (
        <ConnectorListPageSkeleton />
      )}
      {navigationMetadata?.footerMetadata && (
        <RepliconMenuFooter NavigationMetaData={navigationMetadata} />
      )}
    </div>
  );
});
AllConnectorsTab.displayName = 'AllConnectorsTab';
